import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
// import nccInsideImg from "images/GroupCompany/ncc-hero-v4.png";
// import nccInsideImgXs from "images/GroupCompany/hero-img-xs.png";

import nccInsideImg from "images/GroupCompany/ncc-sky.jpeg";
import nccInsideImgXs from "images/GroupCompany/ncc-sky.jpeg";

import { Box } from "@mui/material";
import Header, {
  NavLink,
  NavLinks,
  LogoLink,
  NavToggle,
  DesktopNavLinks,
} from "../headers/light.js";
import StickyHeader from "./StickyHeader.js";

// Styled Components
const StyledHeader = styled(Header)`
  ${tw`pt-8 max-w-none`}
  ${DesktopNavLinks} ${NavLink}, ${LogoLink} {
    ${tw`text-gray-100 hover:border-gray-300 hover:text-gray-300`}
  }
  ${NavToggle}.closed {
    ${tw`text-gray-100 hover:text-primary-500`}
  }
  @media (max-width: 639px) {
    padding-top: 20px;
  }
  @media (max-width: 768px) {
    padding-top: 20px;
  }
`;

// Styled component for OpacityOverlay with conditional small-screen styles
const OpacityOverlay = styled.div`
  ${tw`z-10 absolute inset-0 bg-[#89919b] opacity-25`}
  @media (max-width: 768px) {
    ${tw`bg-[#f9f9f9]`}/* Replace #your-color with the desired background color for small screens */
  }
`;

const HeroContainer = tw.div`z-20 relative px-8 sm:px-12 max-w-screen-xl mx-auto`;
const TwoColumn = tw.div`pt-24 pb-32 px-4 flex justify-between items-center flex-col lg:flex-row`;
const LeftColumn = tw.div`flex flex-col items-center lg:block`;

const SubMenu = styled.div`
  ${tw`hidden absolute p-2 bg-white shadow-lg rounded-lg z-10`}
  min-width: 200px; /* Set consistent width for submenus */
  max-width: 450px; /* Limit width to prevent overflow */
  white-space: nowrap; /* Prevent wrapping */
  overflow: hidden; /* Handle overflow */
  text-overflow: ellipsis; /* Add ellipsis for long text */

  ${NavLink} {
    ${tw`block text-black text-sm p-2`}
    &:hover {
      ${tw`text-primary-500`}
    }
  }
`;

const NavItem = styled.div`
  ${tw`relative inline-block`}
  &:hover ${SubMenu} {
    ${tw`block`}
  }
`;

const Heading = styled.h1`
  ${tw`text-2xl text-center lg:text-left sm:text-3xl lg:text-4xl xl:text-4xl font-black text-gray-100 leading-none`}
  span {
    ${tw`inline-block mt-2`}
  }
`;

const NormalHeadingText = styled.span`
  ${tw`font-normal inline-block mt-2`}
`;

const SlantedBackground = styled.span`
  ${tw`relative text-ncc px-4 -mx-4 py-2`}
  &::before {
    content: "";
    ${tw`absolute inset-0 bg-gray-100 transform -skew-x-12 -z-10`}
  }
`;

const SubmenuBoldText = styled.h4`
  font-weight: 450;
  color: black;
  ${tw`text-sm`}
`;

// Main Component
export default ({ isXs = false }) => {
  const pathName = window.location.pathname;
  const { t } = useTranslation();

  const Container = styled.div`
    ${tw`relative py-2 -mx-8 -mt-8 bg-center bg-cover`};
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    min-height: 50vh;
    background-color: #f9f9f9;

    /* Default background image for screens larger than 768px */
    background-image: url(${nccInsideImg});

    /* Media query for screens 450px or smaller */
    @media (max-width: 450px) {
      background-image: url(${nccInsideImgXs}); /* Use the smaller image for xs screens */
      background-size: contain; /* Ensures the image fits within the container on smaller screens */
      min-height: 40vh;
    }
  `;

  const navLinks = [
    <NavLinks key={1}>
      {/* Service */}
      <NavLink
        style={
          pathName === "/it-service" ? { borderBottom: "2px solid #ffff" } : {}
        }
        href="/it-service"
      >
        {t("header_nav_service")}
      </NavLink>

      {/* Business */}
      <NavItem>
        <NavLink
          style={
            pathName === "/business" ? { borderBottom: "2px solid #fff" } : {}
          }
          href="/business"
        >
          {t("header_nav_business")}
        </NavLink>
        <SubMenu>
          {[
            {
              name: "business.ses.heading",
              link: "/business#system-engineering",
            },
            {
              name: "business.reinforcement.heading",
              link: "/business#reinforcement-system",
            },
            {
              name: "business.lease-of-pc.heading",
              link: "/business#lease-business",
            },
            {
              name: "business.chineseMarketSupport.heading",
              link: "/business#chinese-market",
            },
          ].map((item, index) => (
            <NavLink key={index} href={`${item.link}`}>
              <SubmenuBoldText>{t(item.name)}</SubmenuBoldText>
            </NavLink>
          ))}
        </SubMenu>
      </NavItem>

      {/* About Us */}
      <NavItem>
        <NavLink
          style={
            pathName === "/company" ? { borderBottom: "2px solid #fff" } : {}
          }
          href="company"
        >
          {t("header_nav_aboutus")}
        </NavLink>
        <SubMenu>
          {[
            { name: "submenu_aboutus_profile", link: "/company#profile" },
            { name: "submenu_aboutus_location", link: "/company#location" },
            {
              name: "submenu_aboutus_boardMember",
              link: "/company#board-member",
            },
            { name: "submenu_aboutus_org", link: "/company#org-chart" },
            { name: "submenu_aboutus_partner", link: "/company#suppliers" },
            { name: "submenu_aboutus_history", link: "/company#history" },
          ].map((item, index) => (
            <NavLink key={index} href={`${item.link}`}>
              <SubmenuBoldText>{t(item.name)}</SubmenuBoldText>
            </NavLink>
          ))}
        </SubMenu>
      </NavItem>

      {/* Recruitment */}
      <NavItem>
        <NavLink
          style={
            pathName === "/recruitment"
              ? { borderBottom: "2px solid #fff" }
              : {}
          }
          href="/recruitment"
        >
          {t("header_nav_recruitment")}
        </NavLink>
      </NavItem>

      {/* Contact */}
      <NavItem>
        <NavLink
          style={
            pathName === "/contact" ? { borderBottom: "2px solid #fff" } : {}
          }
          href="/contact"
        >
          {t("header_nav_contactus")}
        </NavLink>
      </NavItem>
    </NavLinks>,
  ];

  return (
    <div>
      {/* Sticky Header (Always Visible) */}
      <Box>
        <StickyHeader navLinks={navLinks} />
      </Box>

      {/* Hero Section (Only on Home Page) */}
      {pathName === "/" && (
        <Container>
          <OpacityOverlay />
          <HeroContainer>
            <StyledHeader links={navLinks} />
            <TwoColumn>
              <LeftColumn>
                <Heading>
                  <NormalHeadingText>
                    {t("home_section1_title")}
                  </NormalHeadingText>
                  <br />
                  <SlantedBackground>
                    {t("home_section1_title_end")}
                  </SlantedBackground>
                </Heading>
              </LeftColumn>
            </TwoColumn>
          </HeroContainer>
        </Container>
      )}
    </div>
  );
};
